import { createFileRoute, redirect } from "@tanstack/react-router";
import { Box } from "@mui/material";
import { useState } from "react";
import {
  ResetPasswordEmailTab,
  SignInTab,
  TabIndex,
} from "@dmca/frontend/widgets/login";
import {
  authClient,
  isSessionValid,
  SESSION_QUERY_KEY,
} from "@dmca/frontend/hooks";
import { object, optional, string } from "valibot";
import { Button } from "@dmca/frontend/components/button";
import { UserPinSignIn } from "./UserPinSignIn";

export const Route = createFileRoute("/_login/login")({
  component: RouteComponent,
  validateSearch: object({
    callbackURL: optional(string()),
  }),
  beforeLoad: async ({ context: { qc }, search }) => {
    const session = await qc.ensureQueryData({
      queryKey: SESSION_QUERY_KEY,
      queryFn: async ({ signal }) => {
        return await authClient.getSession({ fetchOptions: { signal } });
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
      revalidateIfStale: true,
    });
    // Must match the /_authed validity check exactly — if this only tested
    // for the session's existence, an expired session would make /login and
    // /_authed redirect to each other in an infinite loop.
    if (isSessionValid(session)) {
      throw redirect({
        to: search.callbackURL || "/",
      });
    }
  },
  head: () => ({
    meta: [
      {
        title: "Login | DNA Dashboard",
      },
    ],
  }),
});

function RouteComponent() {
  const [activeTab, setActiveTab] = useState<TabIndex>(TabIndex.SIGN_IN);
  const [userPin, setUserPin] = useState(false);
  const { callbackURL } = Route.useSearch();

  return (
    <Box>
      {userPin ? (
        <UserPinSignIn
          callbackURL={callbackURL}
          onBack={() => setUserPin(false)}
        />
      ) : (
        activeTab === TabIndex.SIGN_IN && (
          <SignInTab callbackURL={callbackURL} />
        )
      )}
      {!userPin && activeTab === TabIndex.SIGN_IN && (
        <>
          <Button fullWidth variant="outlined" onClick={() => setUserPin(true)}>
            Sign in with PIN
          </Button>
          <Button
            variant="text"
            sx={{
              textTransform: "none",
              fontSize: "1rem",
              display: "block",
              m: "auto",
            }}
            onClick={() => setActiveTab(TabIndex.RESET_PASSWORD_EMAIL)}
          >
            Reset Password
          </Button>
        </>
      )}
      {activeTab === TabIndex.RESET_PASSWORD_EMAIL && (
        <ResetPasswordEmailTab setTabIndex={setActiveTab} />
      )}
    </Box>
  );
}
